import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  FileText, Users, Receipt, Building2, 
  Check, ArrowRight, Shield, Clock, Zap,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

export const LandingPage = () => {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const features = [
    {
      icon: FileText,
      title: 'Bollettini di Lavoro',
      description: 'Crea e gestisci bollettini con workflow di approvazione integrato'
    },
    {
      icon: Receipt,
      title: 'Fatturazione',
      description: 'Genera fatture professionali in PDF direttamente dai bollettini approvati'
    },
    {
      icon: Users,
      title: 'Gestione Personale',
      description: 'Ruoli personalizzati, calendario assenze e timesheet'
    },
    {
      icon: Building2,
      title: 'Multi-Progetto',
      description: 'Gestisci clienti e progetti con prezzi personalizzati'
    },
    {
      icon: Shield,
      title: 'Sicuro e Affidabile',
      description: 'Dati protetti con backup automatici e accesso sicuro'
    },
    {
      icon: Zap,
      title: 'Veloce e Intuitivo',
      description: 'Interfaccia moderna pensata per la produttività'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">BollettinoX</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Accedi</Button>
              </Link>
              <Link to="/register">
                <Button>
                  Prova Gratis
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Clock size={16} />
            14 giorni di prova gratuita
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Gestionale per<br />
            <span className="text-primary">Bollettini e Fatture</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            La soluzione completa per gestire bollettini di lavoro, fatturazione e personale. 
            Pensato per le aziende svizzere, in CHF.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="text-lg px-8">
                Inizia la Prova Gratuita
                <ChevronRight size={20} className="ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="text-lg px-8">
                Accedi al tuo Account
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-sm text-slate-500">
            Nessuna carta di credito richiesta • Setup in 2 minuti
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Tutto quello che ti serve
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un unico strumento per gestire l'intero flusso di lavoro della tua azienda
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-slate-200 hover:border-primary/50 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-slate-50" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Piani Semplici e Trasparenti
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Scegli il piano più adatto alle tue esigenze. Inizia con 14 giorni di prova PRO gratuita.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative ${plan.name === 'PRO' ? 'border-primary shadow-xl scale-105' : 'border-slate-200'}`}
              >
                {plan.name === 'PRO' && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-primary text-white">
                      CONSIGLIATO
                    </span>
                  </div>
                )}
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.display_name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">CHF {plan.price_monthly_chf}</span>
                    <span className="text-slate-500">/mese</span>
                    <p className="text-sm text-slate-500 mt-1">
                      o CHF {plan.price_yearly_chf}/anno
                    </p>
                  </div>
                  
                  <div className="mb-6 pb-6 border-b border-slate-200">
                    <p className="text-slate-600">
                      {plan.max_users < 0 ? 'Utenti illimitati' : `Fino a ${plan.max_users} utenti`}
                    </p>
                  </div>

                  {plan.features && plan.features.length > 0 && (
                    <ul className="space-y-3 mb-8 text-left">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Check size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link to="/register">
                    <Button 
                      className="w-full" 
                      variant={plan.name === 'PRO' ? 'default' : 'outline'}
                    >
                      Inizia Prova Gratuita
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center mt-8 text-slate-500">
            Tutti i piani includono 14 giorni di prova gratuita con tutte le funzionalità PRO
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Pronto a semplificare la tua gestione?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Inizia oggi con 14 giorni di prova gratuita. Nessun impegno.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="text-lg px-8">
              Crea il tuo Account Gratuito
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white">BollettinoX</span>
            </div>
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} BollettinoX. Tutti i diritti riservati.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/login" className="text-slate-400 hover:text-white text-sm">
                Accedi
              </Link>
              <Link to="/register" className="text-slate-400 hover:text-white text-sm">
                Registrati
              </Link>
              <Link to="/super-admin/login" className="text-slate-500 hover:text-slate-400 text-xs">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
